import React, { FC } from "react";
import styles from "./MediaGallery.module.css";
import { Photo, Video } from "@types";
import { VideoPlayer } from "@/components/VideoPlayer/VideoPlayer";
import { ThumbnailGenerator } from "@utils/ThumbnailGenerator";
// import debugService from "@api/debugService";

interface MediaItem {
  type: "photo" | "video";
  photo?: Photo;
  video?: Video;
  id: string;
}

interface MediaModalProps {
  mediaItem: MediaItem;
  onClose: () => void;
  onNext?: () => void;
  onPrev?: () => void;
  allItems: MediaItem[];
  currentIndex: number;
  onSelectIndex: (index: number) => void;
}

export const MediaModal: FC<MediaModalProps> = ({
  mediaItem,
  onClose,
  onNext,
  onPrev,
  allItems,
  currentIndex,
  onSelectIndex,
}) => {
  const handleModalContentClick = (e: React.MouseEvent) => {
    e.stopPropagation();
  };

  const renderMedia = () => {
    if (mediaItem.type === "photo") {
      return (
        <img
          src={mediaItem.photo!.url}
          alt="Фото товара"
          className={styles.fullSizeMedia}
        />
      );
    } else {
      // Используем отдельный класс для контейнера видеоплеера в модальном окне
      return (
        <div className={styles.videoContainer}>
          <VideoPlayer
            video={mediaItem.video!}
            autoPlay={true}
            onError={(error) => {
              console.error("Ошибка воспроизведения видео в модальном окне", {
                error,
              });
              // debugService.error(
              //   "Ошибка воспроизведения видео в модальном окне",
              //   { error }
              // );
            }}
          />
        </div>
      );
    }
  };

  // Миниатюры для навигации
  const renderModalThumbnails = () => {
    if (allItems.length <= 1) return null;

    return (
      <div className={styles.modalThumbnailContainer}>
        {allItems.map((item, index) => {
          const isVertical =
            item.type === "video" &&
            ThumbnailGenerator.isVerticalVideo(item.video!);

          return (
            <div
              key={item.id}
              className={`${styles.modalThumbnail} ${
                index === currentIndex ? styles.thumbnailActive : ""
              }`}
              onClick={() => onSelectIndex(index)}
            >
              {item.type === "photo" ? (
                <img
                  src={item.photo!.url}
                  alt={`Миниатюра ${index + 1}`}
                  className={styles.thumbnailImage}
                  loading="lazy"
                />
              ) : (
                // Убедимся, что класс videoThumbnail применяется к миниатюрам видео
                <div className={styles.videoThumbnail}>
                  <img
                    src={ThumbnailGenerator.getVideoThumbnailUrl(item.video!)}
                    alt={`Видео миниатюра ${index + 1}`}
                    className={styles.thumbnailImage}
                    loading="lazy"
                    style={{
                      objectFit: isVertical ? "contain" : "cover",
                      backgroundColor: isVertical ? "#000" : "transparent",
                    }}
                    onError={(e) => {
                      if (e.currentTarget) {
                        e.currentTarget.src =
                          ThumbnailGenerator.getDefaultThumbnail();
                      }
                    }}
                  />
                  {/* Иконка воспроизведения только для миниатюр */}
                  <div className={styles.videoIndicator}>▶</div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={handleModalContentClick}>
        <button
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Закрыть"
        >
          &times;
        </button>

        {onPrev && (
          <button
            className={`${styles.navigationButton} ${styles.prevButton}`}
            onClick={onPrev}
            aria-label="Предыдущее медиа"
          >
            &lt;
          </button>
        )}

        {onNext && (
          <button
            className={`${styles.navigationButton} ${styles.nextButton}`}
            onClick={onNext}
            aria-label="Следующее медиа"
          >
            &gt;
          </button>
        )}

        {renderMedia()}
        {renderModalThumbnails()}
      </div>
    </div>
  );
};

export default MediaModal;
